// FILE: src/components/app/bases/BudgetBaseComponent/hooks/useBudgetSummary.js
import * as React from "react";
import { getUserTransactionsCached } from "../../../../../utils/firebase/cache/transactionsCached";

/** ISO yyyy-MM-dd helpers */
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const addDays = (date, n) => {
  const t = new Date(date);
  t.setDate(t.getDate() + n);
  return t;
};
const normalizeTxArray = (maybe) => {
  if (Array.isArray(maybe)) return maybe;
  if (maybe && Array.isArray(maybe.rows)) return maybe.rows;
  if (maybe && Array.isArray(maybe.items)) return maybe.items;
  return [];
};
const num = (v) => (Number.isFinite(+v) ? +v : 0);

/**
 * Summary metrics computed FROM TRANSACTIONS:
 * - monthlyFreeCashFlow: (income - expense) / months over last ~90 days
 * - netWorthTx: sum of signed amounts for all non-transfer transactions over a wide horizon
 */
export default function useBudgetSummary({ userEmail }) {
  const [monthlyFreeCashFlow, setMonthlyFreeCashFlow] = React.useState(0);
  const [netWorthTx, setNetWorthTx] = React.useState(0);

  // Monthly FCF (last ~90 days)
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!userEmail) { setMonthlyFreeCashFlow(0); return; }
      try {
        const to = new Date();
        const from = addDays(to, -90);
        const out = await getUserTransactionsCached({
          userEmail,
          fromDate: toISO(from),
          toDate: toISO(to),
          cap: 5000,
          latestFirst: false,
        });
        const rows = normalizeTxArray(out);
        let income = 0, expense = 0;
        for (const r of rows) {
          const t = String(r?.typeName || r?.type || "").toLowerCase();
          const amt = num(r?.amount);
          if (t.includes("transfer")) continue;
          if (t.includes("income") || amt > 0) income += Math.abs(amt || num(r?.amountAbs));
          else if (t.includes("expense") || amt < 0) expense += Math.abs(amt || num(r?.amountAbs));
        }
        const days = Math.max(1, Math.round((to - from) / (1000 * 60 * 60 * 24)));
        const months = Math.max(1, days / 30);
        if (alive) setMonthlyFreeCashFlow((income - expense) / months);
      } catch {
        if (alive) setMonthlyFreeCashFlow(0);
      }
    })();
    return () => { alive = false; };
  }, [userEmail]);

  // Estimated Net Worth FROM TRANSACTIONS (wide horizon)
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!userEmail) { setNetWorthTx(0); return; }
      try {
        const to = new Date();
        // Fetch a wide range; adjust if you store very long histories
        const from = new Date(2000, 0, 1);
        const out = await getUserTransactionsCached({
          userEmail,
          fromDate: toISO(from),
          toDate: toISO(to),
          cap: 100000,
          latestFirst: false,
        });
        const rows = normalizeTxArray(out);
        // Sum signed amounts; ignore transfers
        let net = 0;
        for (const r of rows) {
          const t = String(r?.typeName || r?.type || "").toLowerCase();
          if (t.includes("transfer")) continue;
          const amt = num(r?.amount);
          if (amt !== 0) net += amt;
          else {
            // fallback on typeName + amountAbs if amount isn't signed
            const a = num(r?.amountAbs);
            if (t.includes("income")) net += a;
            else if (t.includes("expense")) net -= a;
          }
        }
        if (alive) setNetWorthTx(net);
      } catch {
        if (alive) setNetWorthTx(0);
      }
    })();
    return () => { alive = false; };
  }, [userEmail]);

  return { monthlyFreeCashFlow, netWorthTx };
}
